import { useQuery } from "@tanstack/react-query";
import { getWebsiteConfig } from "@/lib/api/handlers/WebsiteConfigHandling";

/**
 * Per-event cache for the published-state + customDomain pair used by the
 * guest-detail drawer's "Open guest website" link. Single useQuery (not two)
 * — we reuse the existing `getWebsiteConfig(eventId, "published,customDomain")`
 * shape so there is no flicker from a published→customDomain serialization,
 * and the network shape stays identical to the pre-RQ behaviour (one call).
 *
 * Why this exists: the drawer remounts on every open/close (AnimatePresence
 * unmount on close), so without caching every guest click fires a fresh GET.
 * For events with the WEBSITE plugin enabled but no saved config that GET
 * used to 404 — see plan ~/.claude/plans/floofy-leaping-pascal.md. After
 * stage A the GET returns 200 + null; this hook caches the result for 5min.
 *
 * Cache invalidation is wired through `invalidateWebsiteKeys.ts` — all four
 * mutation sites that touch the website-config surface (publish toggle,
 * customDomain edit, page CRUD, delete) already call it.
 */
export function useWebsiteSummary(eventId: string, enabled: boolean) {
  const q = useQuery({
    queryKey: ["website", eventId, "summary"] as const,
    queryFn: () => getWebsiteConfig(eventId, "published,customDomain"),
    enabled: enabled && !!eventId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const data = q.data;
  // websiteVisible semantics:
  //  - null while loading (matches the legacy useState<boolean | null>(null)
  //    initial value so consumers can still distinguish "not yet known" from
  //    "known not visible")
  //  - false on missing config (null data) OR ServerError OR published=false
  //  - true only when published===true
  const websiteVisible: boolean | null =
    data === undefined
      ? null
      : data === null
        ? false
        : "isServerError" in data
          ? false
          : data.published === true;

  const customDomain =
    data && !("isServerError" in data) ? data.customDomain : undefined;

  return { websiteVisible, customDomain };
}
